import React, { useState, useEffect } from 'react';
import { timesheetAPI, projectAPI } from '../services/api';
import moment from 'moment';
import '../styles/Dashboard.css';

const EmployeeDashboard = () => {
  const [currentTimesheet, setCurrentTimesheet] = useState(null);
  const [projects, setProjects] = useState([]);
  const [pastTimesheets, setPastTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);

  // NEW: modal state
  const [showAttestation, setShowAttestation] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Optional: Close modal on Escape
  useEffect(() => {
    if (!showAttestation) return;
    const onKey = (e) => e.key === 'Escape' && setShowAttestation(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAttestation]);

  const idOf = (v) => (typeof v === 'string' ? v : v?._id || '');

  const fetchData = async () => {
    setLoading(true);
    try {
      const currentResponse = await timesheetAPI.getCurrentWeek();
      setCurrentTimesheet(currentResponse.data);

      if (currentResponse.data.entries?.length) {
        setEntries(
          currentResponse.data.entries.map(e => ({
            ...e,
            project: idOf(e.project), // <-- force string id
          }))
        );
      } else {
        setEntries([createEmptyEntry()]);
      }

      const projectsResponse = await projectAPI.getAll();
      setProjects(projectsResponse.data.filter(p => p.isActive));

      const pastResponse = await timesheetAPI.getAll();
      setPastTimesheets(pastResponse.data.filter(ts => ts._id !== currentResponse.data._id));
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createEmptyEntry = () => ({
    project: '',
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
    notes: ''
  });

  const handleAddRow = () => {
    const hasEmptyProject = entries.some(e => !e.project);
    if (hasEmptyProject) {
      alert('Finish selecting the project for the current row before adding another.');
      return;
    }
    setEntries([...entries, createEmptyEntry()]);
  };

  const handleRemoveRow = (index) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== index));
    }
  };

  const handleEntryChange = (index, field, value) => {
    const next = [...entries];
    if (days.includes(field)) {
      const num = parseFloat(value) || 0;
      if (num >= 0 && num <= 24) next[index][field] = num;
    } else {
      next[index][field] = field === 'project' ? String(value) : value;
    }
    setEntries(next);
  };

  const calculateRowTotal = (entry) =>
    days.reduce((sum, day) => sum + (parseFloat(entry[day]) || 0), 0);

  const calculateDayTotal = (day) =>
    entries.reduce((sum, entry) => sum + (parseFloat(entry[day]) || 0), 0);

  const calculateGrandTotal = () =>
    entries.reduce((total, entry) => total + calculateRowTotal(entry), 0);

  const handleSave = async () => {
    try {
      const validEntries = entries.filter(e => e.project);
      if (validEntries.length === 0) {
        alert('Please select at least one project');
        return;
      }

      const ids = validEntries.map(e => e.project);
      if (new Set(ids).size !== ids.length) {
        alert('Duplicate projects found. Each project can appear only once.');
        return;
      }

      await timesheetAPI.create({
        weekStarting: currentTimesheet.weekStarting,
        entries: validEntries
      });

      alert('Timesheet saved successfully');
      fetchData();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to save timesheet'}`);
    }
  };

  // NEW: open the modal instead of window.confirm
  const openAttestationModal = () => {
    if (!currentTimesheet?._id) {
      alert('Please save the timesheet first');
      return;
    }
    setShowAttestation(true);
  };

  // NEW: called by modal "Submit" button
  const confirmSubmit = async () => {
    try {
      setSubmitting(true);
      await timesheetAPI.submit(currentTimesheet._id);
      alert('Timesheet submitted successfully');
      setShowAttestation(false);
      fetchData();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to submit timesheet'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getWeekRange = (weekStarting) => {
    const start = moment(weekStarting);
    const end = moment(weekStarting).add(6, 'days');
    return `${start.format('MMM DD')} - ${end.format('MMM DD, YYYY')}`;
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      draft: 'status-draft',
      submitted: 'status-submitted',
      approved: 'status-approved',
      rejected: 'status-rejected'
    };
    return statusClasses[status] || '';
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="employee-dashboard">
      <h2>Employee Timesheet</h2>

      <div className="current-week-section">
        <div className="week-header">
          <h3>Week: {getWeekRange(currentTimesheet?.weekStarting)}</h3>
          <span className={`status-badge ${getStatusBadgeClass(currentTimesheet?.status)}`}>
            {currentTimesheet?.status?.toUpperCase()}
          </span>
        </div>

        {currentTimesheet?.isLocked && (
          <div className="alert alert-info">
            This timesheet is locked. Contact your administrator to make changes.
          </div>
        )}

        {currentTimesheet?.adminNotes && (
          <div className="alert alert-warning">
            Admin Notes: {currentTimesheet.adminNotes}
          </div>
        )}

        <div className="timesheet-form">
          <table className="timesheet-table">
            <thead>
              <tr>
                <th>Project</th>
                {days.map(day => (
                  <th key={day}>{day.charAt(0).toUpperCase() + day.slice(1).substring(0, 2)}</th>
                ))}
                <th>Total</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => (
                <tr key={index}>
                  <td>
                    <select
                      value={entry.project || ''}
                      onChange={(e) => handleEntryChange(index, 'project', e.target.value)}
                      disabled={currentTimesheet?.isLocked}
                      required
                    >
                      <option value="">Select Project</option>
                      {projects
                        .filter(p => p._id === entry.project || !entries.some(e => e.project === p._id))
                        .map(p => (
                          <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                    </select>
                  </td>
                  {days.map((day) => {
                    const dayIndex = days.indexOf(day); // 0 = Monday, 1 = Tuesday, ...
                    const todayIndex = new Date().getDay(); // 0=Sun..6=Sat
                    const adjustedTodayIndex = (todayIndex + 6) % 7; // make Monday=0
                    const isFutureDay = dayIndex > adjustedTodayIndex;

                    return (
                      <td key={day}>
                        <input
                          type="number"
                          value={entry[day]}
                          onChange={(e) => handleEntryChange(index, day, e.target.value)}
                          disabled={currentTimesheet?.isLocked || isFutureDay}
                          min="0"
                          max="24"
                          step="0.25"
                          className="hours-input"
                        />
                      </td>
                    );
                  })}
                  <td className="total-cell">{calculateRowTotal(entry).toFixed(2)}</td>
                  <td>
                    <input
                      type="text"
                      value={entry.notes || ''}
                      onChange={(e) => handleEntryChange(index, 'notes', e.target.value)}
                      disabled={currentTimesheet?.isLocked}
                      placeholder="Notes"
                      className="notes-input"
                    />
                  </td>
                  <td>
                    {!currentTimesheet?.isLocked && entries.length > 1 && (
                      <button
                        onClick={() => handleRemoveRow(index)}
                        className="btn btn-sm btn-danger"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Daily Total</strong></td>
                {days.map(day => (
                  <td key={day}><strong>{calculateDayTotal(day).toFixed(2)}</strong></td>
                ))}
                <td><strong>{calculateGrandTotal().toFixed(2)}</strong></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>

          {!currentTimesheet?.isLocked && (
            <>
              <button onClick={handleAddRow} className="btn btn-secondary">
                + Add Project Row
              </button>

              <div className="form-actions">
                <button onClick={handleSave} className="btn btn-primary">
                  Save Draft
                </button>
                {currentTimesheet?.status === 'draft' && (
                  // OPEN MODAL INSTEAD OF DIRECT SUBMIT
                  <button onClick={openAttestationModal} className="btn btn-success">
                    Submit Timesheet
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="past-timesheets-section">
        <h3>Past Timesheets</h3>
        {pastTimesheets.length === 0 ? (
          <p>No past timesheets found.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Week</th>
                <th>Total Hours</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {pastTimesheets.map(timesheet => (
                <tr key={timesheet._id}>
                  <td>{getWeekRange(timesheet.weekStarting)}</td>
                  <td>{timesheet.totalHours}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(timesheet.status)}`}>
                      {timesheet.status}
                    </span>
                  </td>
                  <td>
                    {timesheet.submittedAt
                      ? moment(timesheet.submittedAt).format('MMM DD, YYYY')
                      : '-'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== MODAL ===== */}
      {showAttestation && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="attestation-title"
          onClick={(e) => {
            // click outside closes
            if (e.target.classList.contains('modal-overlay')) setShowAttestation(false);
          }}
        >
          <div className="modal">
            <h3 id="attestation-title" className="modal-title">Timesheet Attestation</h3>
            <div className="modal-body">
              <p>
                I attest that all contents of this timesheet reflect the true hours that I worked on
                these projects during the past week. I understand that logging incorrect information
                may result in disciplinary action, up to and including termination of employment.
              </p>
              <p><strong>Click “Submit” to proceed or “Cancel” to return to your timesheet draft.</strong></p>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-warning"
                onClick={() => setShowAttestation(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={confirmSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ================== */}
    </div>
  );
};

export default EmployeeDashboard;
